import { useEffect } from 'react';
import { Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useCreatePatient, useUpdatePatient, usePatient } from '@/hooks/usePatients';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import type { PatientFormData } from '@/types/patient';

interface AddNewPatientFormProps {
  editMode?: boolean;
  patientId?: string;
}

/**
 * AddNewPatientForm - Create or edit patient
 */
export function AddNewPatientForm({ editMode = false, patientId }: AddNewPatientFormProps) {
  const setSelectedPatientId = useMeasurementWizardStore((s) => s.setSelectedPatientId);
  const setPatientPanelMode = useMeasurementWizardStore((s) => s.setPatientPanelMode);

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm<PatientFormData>();
  const createPatientMutation = useCreatePatient();
  const updatePatientMutation = useUpdatePatient();
  
  // Fetch patient data if in edit mode
  const { data: patientData, isLoading: isLoadingPatient } = usePatient(editMode ? patientId || null : null);

  // Populate form when patient data loads
  useEffect(() => {
    if (editMode && patientData) {
      // Convert ISO date to yyyy-MM-dd format for date input
      const dateOfBirth = patientData.dateOfBirth 
        ? new Date(patientData.dateOfBirth).toISOString().split('T')[0] 
        : '';
      
      reset({
        fullName: patientData.fullName || '',
        dateOfBirth,
        sex: patientData.sex || '',
      });
    }
  }, [editMode, patientData, reset]);

  const onSubmit = (data: PatientFormData) => {
    const patientData = {
      fullName: data.fullName,
      dateOfBirth: data.dateOfBirth,
      sex: data.sex,
    };

    if (editMode && patientId) {
      updatePatientMutation.mutate(
        { id: patientId, data: patientData },
        {
          onSuccess: (updatedPatient) => {
            setSelectedPatientId(updatedPatient.id);
            setPatientPanelMode('details');
          },
        }
      );
    } else {
      createPatientMutation.mutate(patientData, {
        onSuccess: (newPatient) => {
          setSelectedPatientId(newPatient.id);
          setPatientPanelMode('details');
        },
      });
    }
  };

  const handleCancel = () => {
    setPatientPanelMode('selection');
  };

  const mutation = editMode ? updatePatientMutation : createPatientMutation;

  // Show loading state while fetching patient data in edit mode
  if (editMode && isLoadingPatient) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Edit patient
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading patient data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {editMode ? 'Edit patient' : 'Add new patient'}
      </Typography>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {editMode ? 'Failed to update patient.' : 'Failed to create patient.'}
        </Alert>
      )}

      <TextField
        label="Full name *"
        fullWidth
        {...register('fullName', { required: 'Name is required' })}
        error={!!errors.fullName}
        helperText={errors.fullName?.message}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Date of birth *"
        type="date"
        fullWidth
        InputLabelProps={{ shrink: true }}
        {...register('dateOfBirth', { required: 'Date of birth is required' })}
        error={!!errors.dateOfBirth}
        helperText={errors.dateOfBirth?.message}
        sx={{ mb: 2 }}
      />

      <Controller
        name="sex"
        control={control}
        rules={{ required: 'Sex is required' }}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.sex}>
            <InputLabel>Sex *</InputLabel>
            <Select
              {...field}
              value={field.value || ''}
              label="Sex *"
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
            {errors.sex && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                {errors.sex.message}
              </Typography>
            )}
          </FormControl>
        )}
      />

      <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleCancel}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          fullWidth
          type="submit"
          disabled={mutation.isPending}
        >
          {editMode ? 'Update Patient' : 'Save Patient'}
        </Button>
      </Box>
    </Box>
  );
}